import { useEffect, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "../lib/auth";
import { formatErr } from "../lib/api";
import { toast } from "sonner";
import { ArrowLeft } from "@/components/icons";
import { MEDICAL_SPECIALTIES } from "../lib/specialties";
import {
  AuthCard,
  FlatInput,
  PrimaryButton,
  Divider,
  SocialButton,
  GoogleG,
} from "./Login";

/**
 * Register — mismo estilo minimalista del login.
 * Flujo en 3 pasos dentro de la misma tarjeta:
 *   1) email
 *   2) nombre + especialidad + teléfono
 *   3) contraseña (+ confirm) → submit
 */
export default function Register() {
  const { register } = useAuth();
  const nav = useNavigate();

  const [step, setStep] = useState(1);
  const [form, setForm] = useState({
    email: "",
    name: "",
    specialty: "",
    phone: "",
    password: "",
    confirm: "",
  });
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState("");

  const emailRef = useRef(null);
  const nameRef = useRef(null);
  const pwdRef = useRef(null);

  useEffect(() => {
    setErr("");
    if (step === 1) emailRef.current?.focus();
    else if (step === 2) nameRef.current?.focus();
    else pwdRef.current?.focus();
  }, [step]);

  const set = (k) => (v) =>
    setForm((f) => ({ ...f, [k]: typeof v === "string" ? v : v.target.value }));

  const validateEmail = () => {
    const v = form.email.trim();
    if (!v || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(v)) {
      setErr("Ingresa un correo válido.");
      return false;
    }
    return true;
  };

  const nextFromEmail = (e) => {
    e?.preventDefault();
    if (!validateEmail()) return;
    setStep(2);
  };

  const nextFromProfile = (e) => {
    e?.preventDefault();
    if (!form.name.trim()) return setErr("Ingresa tu nombre completo.");
    if (!form.specialty) return setErr("Selecciona tu especialidad.");
    setStep(3);
  };

  const submit = async (e) => {
    e?.preventDefault();
    setErr("");
    if (form.password.length < 6)
      return setErr("La contraseña debe tener al menos 6 caracteres.");
    if (form.password !== form.confirm)
      return setErr("Las contraseñas no coinciden.");
    setLoading(true);
    try {
      const u = await register({
        name: form.name.trim(),
        email: form.email.trim().toLowerCase(),
        password: form.password,
        phone: form.phone.trim() || null,
        specialty: form.specialty || null,
      });
      if (!u.email_verified) {
        toast.success("Cuenta creada. Revisa tu correo para verificarla.");
        nav("/verifica-tu-correo", { replace: true });
        return;
      }
      toast.success(`¡Bienvenido, ${u.name}!`);
      nav("/onboarding", { replace: true });
    } catch (e2) {
      setErr(formatErr(e2.response?.data?.detail) || e2.message);
    } finally {
      setLoading(false);
    }
  };

  const goBack = () => {
    setErr("");
    setStep((s) => Math.max(1, s - 1));
  };

  return (
    <div className="min-h-screen w-full bg-[#FAFAFC] flex flex-col relative">
      {/* ── Logo top-left absoluto (idéntico a Login) ─────────────── */}
      <div className="absolute top-4 left-6 sm:top-6 sm:left-8">
        <img
          src="/brand/consudoctor-icon.png"
          alt="ConsuDoctor"
          className="w-8 h-8 sm:w-10 sm:h-10 object-contain opacity-70"
        />
      </div>

      {/* ── Cuerpo centrado ────────────────────────────────────────── */}
      <div className="flex-1 flex items-center justify-center px-4 py-16">
        <div className="w-full max-w-[500px] auth-fade-slide">
          {/* Título */}
          <div className="flex flex-col items-center gap-3 mb-8">
            <h1
              data-testid="register-title"
              className="text-[26px] sm:text-[30px] font-medium text-black tracking-tight"
            >
              Crea tu cuenta
            </h1>
            <p className="text-[13px] text-black/50">
              {step === 1 && "Empieza con tu correo"}
              {step === 2 && "Cuéntanos sobre ti"}
              {step === 3 && "Solo falta tu contraseña"}
            </p>
          </div>

          <StepDots step={step} total={3} />

          <form
            onSubmit={
              step === 1 ? nextFromEmail : step === 2 ? nextFromProfile : submit
            }
            className="mt-6 space-y-3"
          >
            {step === 1 && (
              <FlatInput
                label="Email"
                type="email"
                value={form.email}
                onChange={set("email")}
                placeholder="tucorreo@gmail.com"
                inputRef={emailRef}
                testId="reg-email"
                autoComplete="email"
              />
            )}

            {step === 2 && (
              <>
                <FlatInput
                  label="Nombre completo"
                  value={form.name}
                  onChange={set("name")}
                  placeholder="Dr. Juan Pérez"
                  inputRef={nameRef}
                  testId="reg-name"
                  autoComplete="name"
                />
                <SpecialtySelect
                  value={form.specialty}
                  onChange={set("specialty")}
                />
                <FlatInput
                  label="Teléfono (opcional)"
                  type="tel"
                  value={form.phone}
                  onChange={set("phone")}
                  placeholder="+595 999 111 222"
                  testId="reg-phone"
                  autoComplete="tel"
                  required={false}
                />
              </>
            )}

            {step === 3 && (
              <>
                <FlatInput
                  label="Contraseña"
                  type="password"
                  value={form.password}
                  onChange={set("password")}
                  placeholder="Mínimo 6 caracteres"
                  inputRef={pwdRef}
                  testId="reg-password"
                  autoComplete="new-password"
                />
                <FlatInput
                  label="Confirmar contraseña"
                  type="password"
                  value={form.confirm}
                  onChange={set("confirm")}
                  placeholder="Repite la contraseña"
                  testId="reg-confirm"
                  autoComplete="new-password"
                />
              </>
            )}

            {err && (
              <div
                data-testid="reg-error"
                className="text-sm text-red-700 bg-red-50 border border-red-100 rounded-xl px-4 py-2.5"
              >
                {err}
              </div>
            )}

            {/* Botones: Continuar azul + Volver al login negro (paso 1)
             * o Atrás gris (pasos 2/3), lado a lado como en Login. */}
            <div className="flex flex-col sm:flex-row items-stretch gap-2 pt-2">
              <button
                type="submit"
                disabled={loading}
                data-testid={step === 3 ? "reg-submit" : `reg-step-${step}-next`}
                className="flex-1 h-11 rounded-lg bg-[#0071E3] text-white text-[14px] font-semibold hover:bg-[#0077ED] transition disabled:opacity-40 disabled:cursor-not-allowed inline-flex items-center justify-center gap-2"
              >
                {loading ? (
                  <span className="w-4 h-4 border-2 border-white/70 border-t-transparent rounded-full animate-spin" />
                ) : step === 3 ? (
                  "Crear cuenta"
                ) : (
                  "Continuar"
                )}
              </button>

              {step === 1 ? (
                <Link
                  to="/login"
                  data-testid="reg-login-link"
                  className="flex-1 h-11 rounded-lg bg-black hover:bg-black/85 text-white text-[14px] font-semibold inline-flex items-center justify-center transition"
                >
                  Iniciar sesión
                </Link>
              ) : (
                <button
                  type="button"
                  onClick={goBack}
                  data-testid="reg-back-btn"
                  className="sm:w-44 h-11 rounded-lg bg-white border border-black/10 hover:bg-black/[0.03] text-black text-[14px] font-medium inline-flex items-center justify-center gap-1.5 transition"
                >
                  <ArrowLeft className="w-3.5 h-3.5" strokeWidth={1.75} />
                  Atrás
                </button>
              )}
            </div>
          </form>

          {/* Nota de términos (misma que Login) */}
          <div className="mt-10 pt-6 border-t border-black/[0.06]">
            <p className="text-[12px] text-center text-black/50 leading-relaxed">
              Al crear una cuenta o iniciar sesión aceptas nuestros{" "}
              <Link to="/terminos" className="text-black underline underline-offset-2">
                Términos
              </Link>{" "}
              y confirmas haber leído la{" "}
              <Link to="/privacidad" className="text-black underline underline-offset-2">
                Política de Privacidad
              </Link>.
            </p>
          </div>
        </div>
      </div>

      {/* ── Footer estilo Apple (idéntico a Login) ─────────────────── */}
      <footer className="w-full bg-[#F7F8FA] border-t border-black/[0.05]">
        <div className="max-w-[1400px] mx-auto px-6 py-4 flex flex-wrap items-center justify-center gap-x-6 gap-y-2 text-[12px] text-black/50">
          <span>Copyright © {new Date().getFullYear()} ConsuDoctor</span>
          <Link to="/terminos" className="hover:text-black transition">
            Términos de uso
          </Link>
          <Link to="/privacidad" className="hover:text-black transition">
            Política de privacidad
          </Link>
          <Link to="/soporte" className="hover:text-black transition">
            Soporte
          </Link>
        </div>
      </footer>
    </div>
  );
}

// =========================================================================
// Local UI helpers — específicos del registro
// =========================================================================
function StepDots({ step, total }) {
  return (
    <div className="mt-5 flex items-center justify-center gap-1.5">
      {Array.from({ length: total }).map((_, i) => (
        <div
          key={i}
          className={`h-1 rounded-full transition-all ${
            i + 1 === step
              ? "w-6 bg-black"
              : i + 1 < step
                ? "w-3 bg-black/40"
                : "w-3 bg-black/10"
          }`}
        />
      ))}
    </div>
  );
}

function SpecialtySelect({ value, onChange }) {
  return (
    <label className="block">
      <span className="block text-[11px] uppercase tracking-wider text-black/50 font-semibold mb-1.5 px-1">
        Especialidad
      </span>
      <div className="relative">
        <select
          data-testid="reg-specialty"
          value={value}
          onChange={onChange}
          required
          className={`w-full appearance-none bg-[#F2F3F5] hover:bg-[#EDEEF0] focus:bg-white border border-transparent focus:border-[#0071E3] focus:ring-4 focus:ring-[#0071E3]/15 rounded-xl px-4 py-3.5 text-[15px] font-medium outline-none transition-all pr-10 ${
            value ? "text-black" : "text-black/30"
          }`}
        >
          <option value="" disabled className="bg-white text-black/70">
            Selecciona tu especialidad
          </option>
          {MEDICAL_SPECIALTIES.map((s) => (
            <option key={s} value={s} className="bg-white text-black">
              {s}
            </option>
          ))}
        </select>
        <svg
          className="absolute right-4 top-1/2 -translate-y-1/2 w-4 h-4 text-black/40 pointer-events-none"
          viewBox="0 0 20 20"
          fill="currentColor"
        >
          <path
            fillRule="evenodd"
            d="M5.23 7.21a.75.75 0 011.06.02L10 11.06l3.71-3.83a.75.75 0 111.08 1.04l-4.24 4.38a.75.75 0 01-1.08 0L5.21 8.27a.75.75 0 01.02-1.06z"
            clipRule="evenodd"
          />
        </svg>
      </div>
    </label>
  );
}
